/** Keep something from a lesson in your notebook, with a way back to it.
 *
 * The client's word was "reference": a learner should be able to hold on to
 * a rule or a phrase while studying and find it again later. Until now the
 * notebook could store that — the API took a source label and path — but
 * there was no way for a learner to actually put anything in it from a
 * lesson. This is that missing door.
 *
 * It pre-fills with whatever the learner has SELECTED on the page, because
 * that is what they were looking at when they reached for the button. With
 * nothing selected it opens empty and they type.
 */
import { useEffect, useState } from "react";
import { createNote, listSections } from "../../api/notebook";
import { ApiError } from "../../api/http";
import { Button } from "../../components/ui";
import { useToast } from "../../components/ToastContext";
import { useT } from "../../i18n";

export function SaveToNotebook({
  label,
  path,
}: {
  /** What this is, in the notebook list — e.g. "A2.1 Lektion 4". */
  label: string;
  /** Where to go back to. */
  path: string;
}) {
  const t = useT();
  const { push } = useToast();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [section, setSection] = useState("");
  const [sections, setSections] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    listSections().then(setSections).catch(() => undefined);
  }, [open]);

  const start = () => {
    // Whatever they highlighted on the page is almost certainly what they
    // want to keep. Falling back to empty is fine; guessing is not.
    const selected = window.getSelection?.()?.toString().trim() ?? "";
    setText(selected);
    setOpen(true);
  };

  const save = async () => {
    const content = text.trim();
    if (!content) return;
    setSaving(true);
    try {
      await createNote(content, section || null, { label, path });
      push("success", t("notebook.saved"));
      setOpen(false);
      setText("");
    } catch (err) {
      push("error", err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <Button variant="ghost" onClick={start}>
        {t("notebook.saveHere")}
      </Button>
    );
  }

  return (
    <div className="rounded-card border border-line bg-surface p-3">
      <label htmlFor="save-note" className="text-sm font-semibold text-ink">
        {t("notebook.saveTitle")}
      </label>
      <p className="text-xs text-muted">{t("notebook.saveFrom", { lesson: label })}</p>
      <textarea
        id="save-note"
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t("notebook.savePlaceholder")}
        className="mt-2 w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand"
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select
          value={section}
          aria-label={t("notebook.moveTo")}
          onChange={(e) => setSection(e.target.value)}
          className="rounded-control border border-line bg-surface px-2 py-1 text-xs text-muted"
        >
          <option value="">{t("notebook.allNotes")}</option>
          {sections.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={() => void save()} loading={saving} disabled={!text.trim()}>
            {t("common.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
